import { useEffect, useEffectEvent, useState } from "react"

/** The reading column: the prose inside the reading shell's <article>
 *  (web/modules/article-body). The shell renders a bare <article> holding the
 *  prose, then the author strip and the next-step region, so the measure is
 *  the prose itself and the bar reaches 100% at the end of the text, not at the
 *  end of the tail. Articles and the eighteen situation pages both render
 *  their prose there. */
const READING_COLUMN = ".ca-article-body article .prose-cold-approach"

/** Reading-progress percentage (0-100) for the article body on the page, or
 * null when no reading column is present (callers skip the state update in
 * that case rather than flashing to a fake 0). Reads the DOM directly, so
 * call it only from a browser scroll/resize handler. */
export function getArticleReadProgress(): number | null {
  const article = document.querySelector(READING_COLUMN)
  if (!article) return null
  const rect = article.getBoundingClientRect()
  const total = rect.height
  const scrolled = Math.max(0, -rect.top + window.innerHeight * 0.4)
  const ratio = total > 0 ? Math.min(1, Math.max(0, scrolled / total)) : 0
  return Math.round(ratio * 100)
}

/** A number that goes up each render in which any entry of `deps` changed
 * (Object.is, like React's own dependency check). `deps` is a caller-supplied
 * list, which the exhaustive-deps rule cannot read, so the change is detected
 * here and handed to the effect as one plain number it can list. */
function useDepsVersion(deps: unknown[]): number {
  const [held, setHeld] = useState({ deps, version: 0 })
  const changed =
    held.deps.length !== deps.length || deps.some((dep, i) => !Object.is(dep, held.deps[i]))
  if (changed) setHeld({ deps, version: held.version + 1 })
  return held.version
}

/** Runs `handler` once on mount, then on every passive scroll/resize, with
 * cleanup on unmount - the listener registration boilerplate hand-copied
 * across the article reading-progress components. A null `handler` registers
 * nothing, so a caller can skip listening entirely in a state where it would
 * render nothing to update. The listener always calls the handler from the
 * latest render, so a handler that reads a prop or state its caller left out of
 * `deps` is never stale; `deps` only decides when to listen afresh (and
 * measure again straight away), as before. */
export function useScrollAndResize(handler: (() => void) | null, deps: unknown[]) {
  const run = useEffectEvent(() => handler?.())
  const listening = handler !== null
  const depsVersion = useDepsVersion(deps)
  useEffect(() => {
    if (!listening) return
    const onChange = () => run()
    onChange()
    window.addEventListener("scroll", onChange, { passive: true })
    window.addEventListener("resize", onChange)
    return () => {
      window.removeEventListener("scroll", onChange)
      window.removeEventListener("resize", onChange)
    }
  }, [listening, depsVersion])
}
